import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiChip, MiInspectionPopper, MiPopper } from "@miplatform/components";
import { IInspectionTemplate } from "@miplatform/types";
import { getDefaultDate } from "@miplatform/utils";
import { Box, MenuItem, MenuItemProps } from "@mui/material";
import { InspectionOption } from "./useInspectionSearch";

export function InspectionSearchOption({
  option,
  props,
  close,
  templates,
  onSelect,
}: Readonly<InspectionSearchOptionProps>) {
  const { classes, theme } = useStyles();

  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const template = templates[option.templateName];

  const openPopper = (e: React.MouseEvent<HTMLElement>) => {
    const { currentTarget } = e;
    const timeout = setTimeout(() => {
      setAnchorEl(currentTarget);
    }, 250);
    setTimeoutId(timeout);
  };

  const closePopper = () => {
    setAnchorEl(null);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const handleOnClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(option);
    closePopper();
    close();
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (
    <MenuItem
      {...props}
      onClick={handleOnClick}
      className={classes.option}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
    >
      {template && (
        <Box flex={1}>
          <MiChip
            label={template.abbreviation ?? template.shortName ?? "---"}
            style={{
              backgroundColor: template.color,
              color: theme.palette.common.white,
              borderRadius: 4,
            }}
            size="small"
          />
        </Box>
      )}
      <Box flex={5} className={classes.address}>
        {option.home?.address?.addressLine1}
      </Box>
      <Box flex={1}>#{option.attemptNumber}</Box>
      <Box flex={2} color={theme.palette.text.secondary}>
        {option.approvedStartDate
          ? getDefaultDate(option.approvedStartDate)
          : option.attemptStatus}
      </Box>

      <MiPopper
        anchorEl={anchorEl}
        arrowColor={
          templates[option.templateName]?.color ?? theme.palette.primary.main
        }
        placement="left"
        open={Boolean(anchorEl)}
        showArrow
      >
        <MiInspectionPopper
          inspection={option}
          headerColor={templates[option.templateName]?.color}
        />
      </MiPopper>
    </MenuItem>
  );
}

interface InspectionSearchOptionProps {
  option: InspectionOption;
  props: MenuItemProps;
  close: () => void;
  templates: { [key: string]: IInspectionTemplate };
  onSelect: (inspection: InspectionOption) => void;
}

const useStyles = makeStyles()((theme) => ({
  option: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    cursor: "pointer",
  },
  address: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
